import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Bell, CheckCheck, Mail, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const InternalMessageCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const isEligibleUser = Boolean(user);

  const unreadCountQuery = useQuery(
    ['internal-messages-unread-count'],
    () => api.get('/messages/unread-count'),
    {
      enabled: isEligibleUser,
      retry: false,
    }
  );

  const messagesQuery = useQuery(
    ['internal-messages'],
    () => api.get('/messages'),
    {
      enabled: isEligibleUser && open,
      retry: false,
      onError: (error) => {
        toast.error(error.response?.data?.message || '쪽지함을 불러오지 못했습니다.');
      },
    }
  );

  const refreshMessages = () => {
    queryClient.invalidateQueries('internal-messages');
    queryClient.invalidateQueries('internal-messages-unread-count');
  };

  const markReadMutation = useMutation(
    (messageId) => api.post(`/messages/${messageId}/read`),
    {
      onSuccess: () => {
        refreshMessages();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '쪽지를 읽음 처리하지 못했습니다.');
      },
    }
  );

  const markAllReadMutation = useMutation(
    () => api.post('/messages/read-all'),
    {
      onSuccess: () => {
        refreshMessages();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '전체 읽음 처리에 실패했습니다.');
      },
    }
  );

  const unreadCount = unreadCountQuery.data?.unread_count || 0;
  const messages = messagesQuery.data?.messages || [];

  const handleOpen = () => {
    setOpen(true);
    refreshMessages();
  };

  const handleMessageClick = async (message) => {
    if (!message.is_read) {
      await markReadMutation.mutateAsync(message.id);
    }

    if (message.link_url) {
      navigate(message.link_url);
      setOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="relative rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-gray-900/30 px-4 py-20">
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">쪽지함</h3>
                <p className="mt-1 text-xs text-gray-500">구독료 안내와 시스템 알림을 확인할 수 있습니다.</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center justify-between bg-gray-50 px-5 py-3">
              <span className="text-sm text-gray-600">읽지 않은 쪽지 {unreadCount}건</span>
              <button
                type="button"
                onClick={() => markAllReadMutation.mutate()}
                disabled={unreadCount === 0 || markAllReadMutation.isLoading}
                className="inline-flex items-center rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <CheckCheck className="mr-1.5 h-4 w-4" />
                모두 읽음
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              {messagesQuery.isLoading ? (
                <div className="px-5 py-12 text-center text-sm text-gray-500">쪽지를 불러오는 중입니다.</div>
              ) : messages.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-gray-500">도착한 쪽지가 없습니다.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {messages.map((message) => (
                    <button
                      key={message.id}
                      type="button"
                      onClick={() => handleMessageClick(message)}
                      className={`block w-full px-5 py-4 text-left hover:bg-gray-50 ${
                        message.is_read ? 'bg-white' : 'bg-primary-50/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 rounded-lg p-2 ${message.is_read ? 'bg-gray-100 text-gray-500' : 'bg-primary-100 text-primary-700'}`}>
                          <Mail className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-semibold text-gray-900">{message.subject}</p>
                            {!message.is_read && (
                              <span className="mt-0.5 inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                            )}
                          </div>
                          <p className="mt-1 line-clamp-3 text-sm text-gray-600">{message.body}</p>
                          <p className="mt-2 text-xs text-gray-500">{formatDateTime(message.created_at)}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch (error) {
    return value;
  }
};

export default InternalMessageCenter;
